import { useMemo } from 'react';
import { useComponentInfoData } from '../ComponentInfo/useComponentInfoData';
import { useSechubData } from '../SechubIntegration/useSechubData';
import { useBlackduckData } from '../BlackduckIntegration/useBlackduckData';
import { useSonarQubeData } from '../SonarQubeIntegration/useSonarQubeData';
import { usePagerDutyData } from '../PagerDutyIntegration/usePagerDutyData';
import { CatalogEntityYaml } from './types';
import { ComponentInfoData } from '../ComponentInfo/types';
import { SechubData } from '../SechubIntegration/types';
import { BlackduckData } from '../BlackduckIntegration/types';
import { SonarQubeData } from '../SonarQubeIntegration/types';
import { PagerDutyData } from '../PagerDutyIntegration/types';

interface YamlPreviewDataOptions {
  repositoryValue: string;
  infrastructureDependencies?: string[];
  providesApis?: string[];
  sharedComponentData?: ComponentInfoData | null;
  sharedSechubData?: SechubData | null;
  sharedBlackduckData?: BlackduckData | null;
  sharedSonarqubeData?: SonarQubeData | null;
  sharedPagerdutyData?: PagerDutyData | null;
}

export const useYamlPreviewData = ({
  repositoryValue,
  infrastructureDependencies = [],
  providesApis = [],
  sharedComponentData,
  sharedSechubData,
  sharedBlackduckData,
  sharedSonarqubeData,
  sharedPagerdutyData,
}: YamlPreviewDataOptions): {
  yamlData: CatalogEntityYaml | null;
  yamlString: string;
  loading: boolean;
} => {
  // Get data from hooks only if shared data is not available
  const { data: hookComponentData, loading: componentLoading } =
    useComponentInfoData(repositoryValue);
  const { data: hookSechubData, loading: sechubLoading } =
    useSechubData(repositoryValue);
  const { data: hookBlackduckData, loading: blackduckLoading } =
    useBlackduckData(repositoryValue);
  const { data: hookSonarqubeData, loading: sonarqubeLoading } =
    useSonarQubeData(repositoryValue);
  const { data: hookPagerdutyData, loading: pagerdutyLoading } =
    usePagerDutyData(repositoryValue);

  // Use shared data if available, otherwise fall back to hook data
  const componentData = sharedComponentData || hookComponentData;
  const sechubData = sharedSechubData || hookSechubData;
  const blackduckData = sharedBlackduckData || hookBlackduckData;
  const sonarqubeData = sharedSonarqubeData || hookSonarqubeData;
  const pagerdutyData = sharedPagerdutyData || hookPagerdutyData;

  const loading =
    componentLoading ||
    sechubLoading ||
    blackduckLoading ||
    sonarqubeLoading ||
    pagerdutyLoading;

  // Generate YAML data structure
  const yamlData = useMemo((): CatalogEntityYaml | null => {
    if (!repositoryValue) {
      return null;
    }

    // Build annotations object
    const annotations: CatalogEntityYaml['metadata']['annotations'] = {
      'datadoghq.mercedes-benz.com/enabled': 'true',
    };

    // Add annotations from integration data
    if (componentData?.githubRepository) {
      annotations['github.com/project-slug'] = componentData.githubRepository;
    }

    if (sechubData?.projectKey) {
      annotations['mercedes-benz.com/sechub-project-key'] =
        (sechubData.provider ? `${sechubData.provider}/` : '') +
        sechubData.projectKey;
    }

    if (sonarqubeData?.projectKey) {
      annotations['sonarqube.org/project-key'] =
        (sonarqubeData.provider ? `${sonarqubeData.provider}/` : '') +
        sonarqubeData.projectKey;
    }

    if (blackduckData?.projectKey) {
      annotations['synopsys.com/project-key'] =
        (blackduckData.provider ? `${blackduckData.provider}/` : '') +
        blackduckData.projectKey;
    }

    if (pagerdutyData?.serviceId) {
      annotations['datadoghq.mercedes-benz.com/pagerduty'] =
        `${pagerdutyData.serviceId}`;
    }

    // Build dependsOn array for spec
    const dependsOn = infrastructureDependencies.map(entityRef => entityRef);

    const yaml: CatalogEntityYaml = {
      apiVersion: 'backstage.io/v1alpha1',
      kind: 'Component',
      metadata: {
        namespace: 'default',
        annotations,
        name: componentData?.componentName || 'component-name',
        title: componentData?.componentTitle || 'Component Title',
        description:
          componentData?.componentDescription || 'Component Description',
      },
      spec: {
        type: 'service',
        system: `system:default/${componentData?.tenant}`,
        owner: `group:default/${componentData?.owner}`,
        lifecycle: 'production',
      },
    };

    // Add providesApis if there are any selected APIs
    if (providesApis.length > 0) {
      yaml.spec!.providesApis = providesApis;
    }

    // Add spec.dependsOn if there are infrastructure dependencies
    if (dependsOn.length > 0) {
      yaml.spec!.dependsOn = dependsOn;
    }

    return yaml;
  }, [
    repositoryValue,
    componentData,
    sechubData,
    blackduckData,
    sonarqubeData,
    pagerdutyData,
    infrastructureDependencies,
    providesApis,
  ]);

  // Generate YAML string using a deterministic serializer to ensure correct layout
  const yamlString = useMemo(() => {
    if (!yamlData) {
      return '';
    }

    const quoteIfNeeded = (v: any): string => {
      if (v === null || v === undefined) return '""';
      const s = String(v);
      // Quote if contains colon, slash, @, or is true/false
      if (
        s.includes(':') ||
        s.includes('/') ||
        s.includes('@') ||
        s === 'true' ||
        s === 'false'
      ) {
        return `"${s}"`;
      }
      return s;
    };

    const lines: string[] = [];

    // apiVersion and kind
    lines.push(`apiVersion: ${quoteIfNeeded(yamlData.apiVersion)}`);
    lines.push(`kind: ${yamlData.kind}`);

    // metadata block
    lines.push('metadata:');
    const md = yamlData.metadata || {};
    lines.push(`  namespace: ${md.namespace || 'default'}`);

    // annotations block - ensure annotations: is on its own line
    const ann = md.annotations || {};
    if (Object.keys(ann).length > 0) {
      lines.push('  annotations:');
      // maintain insertion order
      Object.keys(ann).forEach(key => {
        const a = ann as Record<string, any>;
        lines.push(`    ${key}: ${quoteIfNeeded(a[key])}`);
      });
    }

    // other metadata fields
    if (md.name) lines.push(`  name: ${quoteIfNeeded(md.name)}`);
    if (md.title) lines.push(`  title: ${quoteIfNeeded(md.title)}`);
    if (md.description)
      lines.push(`  description: ${quoteIfNeeded(md.description)}`);

    // spec block
    if (yamlData.spec) {
      lines.push('spec:');
      lines.push(`  type: ${yamlData.spec.type}`);
      lines.push(`  system: ${quoteIfNeeded(yamlData.spec.system)}`);
      lines.push(`  owner: ${quoteIfNeeded(yamlData.spec.owner)}`);
      lines.push(`  lifecycle: ${yamlData.spec.lifecycle}`);

      // Add providesApis if they exist
      if (yamlData.spec.providesApis && yamlData.spec.providesApis.length > 0) {
        lines.push('  providesApis:');
        yamlData.spec.providesApis.forEach(apiRef => {
          lines.push(`    - ${quoteIfNeeded(apiRef)}`);
        });
      }
    }

    // spec.dependsOn - prefer dependsOn inside spec rather than relations
    if (yamlData.spec?.dependsOn && yamlData.spec.dependsOn.length > 0) {
      lines.push('  dependsOn:');
      yamlData.spec.dependsOn.forEach(ref => {
        lines.push(`    - ${quoteIfNeeded(ref)}`);
      });
    }

    return lines.join('\n');
  }, [yamlData]);

  return { yamlData, yamlString, loading };
};
